import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform, View } from "react-native";
import { useCatalogue } from "@clearabee/ui-sdk";
import { ErrorPage } from "components/ErrorPage/ErrorPage";
import { Layout } from "components/Layout/Layout";
import { useForm, Form } from "lib/hooks/useForm";
import {
  FormState,
  QueryParams,
  queryToState,
  stateToQuery,
  schema,
} from "./Skips.validation";
import { SelectType } from "./SelectType";
import { RefAndPostcode } from "./RefAndPostcode";
import { SelectProductGrid } from "./SelectProductGrid";
import { Question } from "components/Question/Question";
import { QuestionContextProvider } from "lib/providers/questionProvider";
import { WasteTypeAndPostcode } from "./WasteTypeAndPostcode";
import { formatPostcode } from "lib/utils";
import { SKIP_WASTE_TYPES } from "lib/constants/skipWasteTypes";
import { styles } from "./Skips.styles";
import type { TFuncKey } from "react-i18next";
import { SelectExchangeGrid } from "./SelectExchangeGrid";
import { PreviousSkip } from "./PreviousSkip";
import { WasteType } from "./WasteType";
import { LoadQueryParams } from "./LoadQueryParams";
import isEqual from "fast-deep-equal";
import { useRouter } from "lib/hooks/useRouter";
import { DisplaySkipHireInstructions } from "./DisplaySkipHireInstructions";

const SkipQuestion = Question<FormState>();

interface SkipsProps {
  queryParams?: QueryParams;
}

export const Skips = ({
  queryParams: initialQueryParams = {},
}: SkipsProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { defaultCatalogue: catalogue } = useCatalogue();
  const service = catalogue.items.find(({ sku }) => sku === "SKIPS");
  const queryParams = queryToState(initialQueryParams);
  const { navigate, replace } = useRouter();
  const [displaySkipHireInstructions, setDisplaySkipHireInstructions] =
    useState(false);

  const form = useForm({
    validationSchema: schema,
    defaultValues: queryParams ?? {},
    reValidateMode: "onChange",
  });
  const { getValues } = form;
  const valuesRef = useRef(getValues());

  useEffect(() => {
    const values = getValues();

    if (isEqual(values, valuesRef.current)) {
      return;
    }

    valuesRef.current = values;

    const fn = Platform.OS === "web" ? replace : navigate;
    const params = { ...stateToQuery(values), categorySku: "SKIPS" };

    fn({ routeName: "ServicePage", params, shallow: true });
  });

  if (!service) return <ErrorPage />;

  /**
   * if the type is skip hire, display the instructions
   */
  const isSkipHire = getValues().type === "SKIPHIRE";

  useEffect(() => {
    if (!isSkipHire) return;

    setDisplaySkipHireInstructions(true);
  }, [isSkipHire]);

  return (
    <>
      <Layout
        title={translate(
          `hwa.screens.${
            displaySkipHireInstructions
              ? "skipHireInstructions"
              : "selectProduct"
          }.title`,
        )}
        backTo={{ routeName: "Book" }}
        contentWidth="xlarge3"
      >
        <Form state={form}>
          <QuestionContextProvider initVisibleState={{ type: "open" }}>
            <LoadQueryParams queryParams={queryParams} />

            <View style={styles.smallQuestion}>
              {!displaySkipHireInstructions && (
                <SkipQuestion
                  question={translate("hwa.components.skips.questions.type")}
                  id="type"
                  formatAnswer={(item) =>
                    item === "SKIPHIRE" ? "Skip Hire" : "Skip Exchange"
                  }
                >
                  <SelectType
                    setDisplaySkipHireInstructions={
                      setDisplaySkipHireInstructions
                    }
                  />
                </SkipQuestion>
              )}

              <SkipQuestion
                question={translate(
                  "hwa.components.skips.questions.refAndPostcode",
                )}
                previousId="type"
                id="refAndPostcode"
                formatAnswer={({ postcode, ref }) => {
                  return `${ref.toUpperCase()} - ${postcode.toUpperCase()}`;
                }}
              >
                <RefAndPostcode />
              </SkipQuestion>

              <SkipQuestion
                question={translate("hwa.components.skips.questions.wasteType")}
                previousId="refAndPostcode"
                id="wasteType"
                formatAnswer={(wasteType) =>
                  `${translate(
                    SKIP_WASTE_TYPES.find(({ value }) => wasteType === value)
                      ?.label as TFuncKey,
                  )}`
                }
              >
                <WasteType />
              </SkipQuestion>

              {!displaySkipHireInstructions && (
                <SkipQuestion
                  question={translate(
                    "hwa.components.skips.questions.wasteTypeAndPostcode",
                  )}
                  previousId="type"
                  id="wasteTypeAndPostcode"
                  formatAnswer={({ postcode, wasteType }) => {
                    return `${translate(
                      SKIP_WASTE_TYPES.find(({ value }) => value === wasteType)
                        ?.label as TFuncKey,
                    )} - ${formatPostcode(postcode)}`;
                  }}
                >
                  <WasteTypeAndPostcode />
                </SkipQuestion>
              )}
            </View>

            <SkipQuestion
              question={translate("hwa.components.skips.questions.selectSkip")}
              id="exchange"
              formatAnswer={(value) => value.toString()}
            >
              <>
                <PreviousSkip />
                <SelectExchangeGrid />
              </>
            </SkipQuestion>

            <SkipQuestion
              question={translate("hwa.components.skips.questions.selectSkip")}
              previousId="wasteTypeAndPostcode"
              id="product"
            >
              <SelectProductGrid />
            </SkipQuestion>
          </QuestionContextProvider>
        </Form>

        {/* DISPLAY SKIP HIRE INSTRUCTIONS */}
        {displaySkipHireInstructions && (
          <DisplaySkipHireInstructions
            onClickContinue={() => setDisplaySkipHireInstructions(false)}
          />
        )}
      </Layout>
    </>
  );
};
